/**
 * What a full-screen overlay (the phone's menu) needs of the page under it: the page held still while it's open (and
 * back where it was after), and the keyboard's focus kept inside it.
 */

/**
 * Holds the page still: it neither moves nor scrolls under the overlay (iOS Safari scrolls a page with `overflow:
 * hidden` anyway, so the body is pinned where it was). Returns how to let it go: back at the same scroll position, or
 * at `to` (the top of a page gone to from the overlay).
 */
export function lockScroll(doc: Document = document): (to?: number) => void {
  const win = doc.defaultView ?? globalThis;
  const y = win.scrollY;
  const body = doc.body;
  const before = body.getAttribute("style");
  Object.assign(body.style, { position: "fixed", top: `-${y}px`, left: "0", right: "0", overflow: "hidden" });
  return (to = y) => {
    if (before === null) {
      body.removeAttribute("style");
    } else {
      body.setAttribute("style", before);
    }
    win.scrollTo(0, to);
  };
}

/** What the Tab key can reach. */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** The elements inside `root` the Tab key reaches, in order (those hidden or inert left out). */
export function focusables(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (el) => !(el.closest("[inert]") || el.closest("[hidden]") || el.getAttribute("aria-hidden") === "true"),
  );
}

/**
 * Keeps Tab inside `root`: from its last element (or from outside it) Tab goes to its first, and Shift+Tab from its
 * first to its last. Call it on keydown; it handles (and prevents) only the Tab presses that would leave.
 */
export function trapTab(root: HTMLElement, e: KeyboardEvent): void {
  if (e.key !== "Tab") {
    return;
  }
  const list = focusables(root);
  const first = list[0];
  const last = list.at(-1);
  if (!(first && last)) {
    e.preventDefault();
    return;
  }
  const at = root.ownerDocument.activeElement;
  const inside = at instanceof HTMLElement && root.contains(at);
  if (e.shiftKey && (at === first || !inside)) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && (at === last || !inside)) {
    e.preventDefault();
    first.focus();
  }
}
